import React from 'react';
import { Box, Button, ContentTemplate, Grid, MUIDataTableOptions, Typography } from 'web-library';
import { TFunction, useTranslation } from 'react-i18next';


import { makeStyles } from 'tss-react/mui';
import { useHistory } from 'react-router-dom';
import { CandidateResultReleaseSchema } from '../../models/@candidate/CandidateResultReleaseSchema';
import CustomDataTable from '../../components/datatables/CustomDataTable';
import ExamDetail from '../../components/ExamDetail';
import SimpleCandidateDetail from '../../components/SimpleCandidateDetail';
import key from '../../routes/key';
import AdditionalResultLinks from './AdditionalResultLinks';
import ResultSlipExplanitory from '../../components/ResultSlipExplanitory';

export interface IndividualResultReleaseProps {
  candidateResultData: CandidateResultReleaseSchema;
  eligibleForResit: boolean;
  eligibleForAppeal: boolean;
  listingCode?: any;
  examLevel: string;
}

const useStyles = makeStyles()((theme) =>
({
  container: {},
  examDetailsContainer: {},
  resultTable: {
    paddingBottom: theme.spacing(7),
  },
  slipExplanitoryContainer: {
    paddingTop: theme.spacing(2),
  },
  remarks: {
    marginTop: theme.spacing(2),
  },
}));

/** define datatable options
 * */
const dataTableOptions: MUIDataTableOptions = {
  elevation: 3,
  filter: false,
  search: false,
  confirmFilters: false,
  download: false,
  print: false,
  viewColumns: false,
  selectableRows: 'none',
  responsive: 'standard',
  pagination: false,
};

const { result } = key.child;
const { coordinatorResultList, coordinatorResultRelease } = key.result.child;

const tableColumns = (
  candidateResultData: CandidateResultReleaseSchema,
  t: TFunction<'translation'>
) => [
    {
      name: 'subject',
      label: t('exam->>ResultDetail.Subject'),
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          const { subject, subjectCode } = candidateResultData.subjectResultList[dataIndex];
          return (
            <Box>
              <>{subject} </>
              <>{`(${subjectCode})`}</>
            </Box>
          );
        },
      },
    },
    {
      name: 'gradeAlphabetical',
      label: t('exam->>ResultDetail.Grade Alphabetical'),
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          const { hurdleGrade } = candidateResultData.subjectResultList[dataIndex];
          const { gradeAlphabetical } = candidateResultData.subjectResultList[dataIndex];
          if (hurdleGrade) {
            return (
              <div>
                <div>{gradeAlphabetical}</div>
                <div>{hurdleGrade}</div>
              </div>
            );
          }
          return <div>{gradeAlphabetical}</div>;
        },
      },
    },
    {
      name: 'gradeNumerical',
      label: t('exam->>ResultDetail.Grade Numerical'),
    },
    {
      name: 'languageMedium',
      label: t('exam->>ResultDetail.Language Medium'),
    },
    {
      name: 'examiningAuthority',
      label: t('exam->>ResultDetail.Examining Authority'),
    },
  ];

const GeneralResults = ({
  candidateResultData,
  eligibleForResit,
  eligibleForAppeal,
  listingCode,
  examLevel,
}: IndividualResultReleaseProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  return (
    <Grid container direction="row">
      <Grid xs={12}>
        <ContentTemplate isContinuable>
          <Box className={classes.examDetailsContainer}>
            <ExamDetail
              examSeries={candidateResultData.examSeries}
              examLevel={candidateResultData.examLevel}
              year={candidateResultData.examYear}
              school={candidateResultData.examSchool}
            />
          </Box>
        </ContentTemplate>
      </Grid>
      <Grid xs={12}>
        <ContentTemplate continuous isContinuable isDividerTop>
          <SimpleCandidateDetail
            name={candidateResultData.candidateName}
            id={candidateResultData.candidateId}
            indexNo={candidateResultData.candidateIndexNum}
            citizenshipType={candidateResultData.citizenshipType}
          />
        </ContentTemplate>
      </Grid>
      <Grid xs={12}>
        <ContentTemplate type="full" continuous>
          <Box className={classes.resultTable}>
            <CustomDataTable
              dataTableType="datatable"
              columns={tableColumns(candidateResultData, t)}
              data={candidateResultData.subjectResultList}
              options={dataTableOptions}
              title=""
            />
            {candidateResultData.examCode === 'GCEN' && candidateResultData.remarkGceN && (
              <>
                <Typography variant="body1" align="right" className={classes.remarks}>
                  <b>Remarks: </b>
                  {candidateResultData.remarkGceN}
                </Typography>
              </>
            )}
            <Grid className={classes.slipExplanitoryContainer}>
              <ResultSlipExplanitory examLevel={examLevel} />
            </Grid>
            <AdditionalResultLinks
              eligibleForAppeal={eligibleForAppeal}
              eligibleForResit={eligibleForResit}
            />
          </Box>

          {listingCode && (
            <Button
              variant="outlined"
              onClick={() =>
                history.push(
                  `/${result}/${coordinatorResultList.route}/${coordinatorResultRelease.route}`,
                  listingCode
                )
              }
            >
              Back to List
            </Button>
          )}
        </ContentTemplate>
      </Grid>
    </Grid>
  );
};

export default GeneralResults;
